import { z } from "zod";

export function safeParseResult<Input, Output>(
    result: z.SafeParseReturnType<Input, Output>
): {
    success: (typeof result)["success"];
    data: z.SafeParseSuccess<Output>["data"] | undefined;
    error: z.SafeParseError<Input>["error"] | undefined;
} {
    return result.success ? { ...result, error: undefined } : { ...result, data: undefined };
}

const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

type Literal = z.infer<typeof literalSchema>;

type Json = Literal | { [key: string]: Json } | Json[];

const jsonSchema: z.ZodType<Json> = z.lazy(() => z.union([literalSchema, z.array(jsonSchema), z.record(jsonSchema)]));

export const json = () => jsonSchema;

const stringToJSONSchema = z.string().transform((str, ctx): z.infer<typeof jsonSchema> => {
    try {
        const result: z.infer<typeof jsonSchema> = JSON.parse(str);
        return result;
    } catch (e) {
        ctx.addIssue({ code: "custom", message: "Invalid JSON" });
        return z.NEVER;
    }
});

export const stringToJSON = () => stringToJSONSchema;

export function safeJsonParse<T>(encoded: string, schema: z.ZodType<T>) {
    const result = stringToJSONSchema.safeParse(encoded);
    if (result.success) {
        const objectResult = safeParseResult(schema.safeParse(result.data));
        return objectResult.data;
    }

    return undefined;
}

export function safeJsonParseObject(encoded: string) {
    return safeJsonParse(encoded, z.object({}).passthrough());
}
